import React, { useContext, useState, useEffect } from "react";
import AuthContext from "./AuthContext";

// Function to create Stripe checkout session and redirect user
const useStripeCheckout = () => {
  const { user } = useContext(AuthContext);

  const getBackendUrl = () => {
    return process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
  };

  const createStripeSessionAndRedirect = async (packageId, packageType) => {
    if (!user) {
      alert("You must be logged in to subscribe.");
      return;
    }
    
    try {
      const originUrl = window.location.origin;
      const response = await fetch(`${getBackendUrl()}/api/checkout/session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ 
          package_id: packageId,
          package_type: packageType,
          origin_url: originUrl
        }),
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to create Stripe session');
      }
      
      const data = await response.json();
      
      if (data.url) {
        window.location.href = data.url;
      } else if (data.message) {
        // Handle free tier activation
        alert(data.message);
        if (data.redirect_url) {
          window.location.href = data.redirect_url;
        }
      } else {
        throw new Error('No Stripe URL returned');
      }
    } catch (error) {
      alert(`Error: ${error.message}`);
    }
  };

  return createStripeSessionAndRedirect;
};

const subscriptionPlans = [
  {
    title: "Explorer",
    price: "$0",
    description: "Free Tier",
    features: [
      "Access to basic Q&A with 1 agent (rotating monthly)",
      "10 questions per month",
      "1 USCIS notice scan/month",
      "Access to emotional support chatbot Ada",
      "Refer a friend to unlock +5 questions",
    ],
    packageId: "Explorer"
  },
  {
    title: "Navigator",
    price: "$39",
    description: "Basic Tier",
    features: [
      "Full access to 1 AI agent",
      "200 AI interactions/month",
      "15 document uploads/month",
      "Document upload and review",
      "AI-generated cover letters and evidence summaries",
      "In-depth case analysis (strength scores, red flags, improvement plans)",
      "RFE/NOID response",
    ],
    packageId: "Navigator"
  },
  {
    title: "Advocate",
    price: "$79",
    description: "Professional Tier (Most Popular)",
    mostPopular: true,
    features: [
      "Access to 4 AI agents (e.g., Family, Work, Asylum)",
      "500 AI interactions/month",
      "20 document uploads/month",
      "AI-generated cover letters and evidence summaries",
      "Advanced form-filling Assistant",
      "RFE/NOID response",
      "In-depth case analysis",
      "Export completed forms in multiple formats",
    ],
    packageId: "Advocate"
  },
  {
    title: "Champion",
    price: "$149",
    description: "Premium Tier",
    features: [
      "Unlimited access to all 8 AI agents",
      "Everything in Professional tier",
      "Unlimited questions across all agents",
      "Unlimited document uploads and reviews",
      "In-depth case analysis",
      "Unlimited document scans + NOID/RFE response drafting",
      "AI-generated cover letters and evidence summaries",
      "Integration with attorney network (Coming soon)",
      "Priority email support (24-hour response)",
    ],
    packageId: "Champion"
  },
];

const payPerUseAddOns = [
  {
    title: "RFE/NOID Response Draft",
    price: "$29.99 per response",
    features: [
      "AI-generated draft letter tailored to USCIS concerns",
      "Evidence checklist + suggested affidavits",
      "Red flag detection",
      "Risk flags + improvement recs",
      "Case strength assessment (strength scores, red flags, improvement plans)",
      "Full response packet generator (USCIS ready)",
    ],
    packageId: "rfe_noid_response"
  },
  {
    title: "AI Case Review",
    price: "$29.99 per review",
    features: [
      "Upload case details/documents → get a strength score",
      "Weakness report with analysis",
      "AI-generated draft letter tailored to USCIS concerns",
      "Evidence checklist + suggested affidavits",
      "Red flag detection",
      "Risk flags + improvement recs",
      "Case strength assessment (strength scores, red flags, improvement plans)",
      "Full response packet generator (USCIS ready)",
    ],
    packageId: "ai_case_review"
  },
];

const PricingSection = () => {
  const createStripeSessionAndRedirect = useStripeCheckout();
  const { user } = useContext(AuthContext);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [loadingStates, setLoadingStates] = useState({});

  const getBackendUrl = () => {
    return process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
  };

  useEffect(() => {
    const fetchCurrentSubscription = async () => {
      if (user) {
        try {
          const response = await fetch(`${getBackendUrl()}/api/subscription/current`, {
            credentials: 'include',
          });
          
          if (response.ok) {
            const data = await response.json();
            setCurrentSubscription(data.subscription);
          }
        } catch (error) {
          console.error('Error fetching subscription:', error);
        }
      }
    };

    fetchCurrentSubscription();
  }, [user]);

  const handleSubscriptionClick = async (plan) => {
    if (!user) {
      alert("Please log in to subscribe.");
      return;
    }

    setLoadingStates(prev => ({ ...prev, [plan.title]: true }));
    
    try {
      await createStripeSessionAndRedirect(plan.packageId, "subscription");
    } catch (error) {
      console.error('Subscription error:', error);
      alert('Error processing subscription. Please try again.');
    } finally {
      setLoadingStates(prev => ({ ...prev, [plan.title]: false }));
    }
  };

  const handleAddonClick = async (addon) => {
    if (!user) {
      alert("Please log in to purchase add-ons.");
      return;
    }

    setLoadingStates(prev => ({ ...prev, [addon.title]: true }));
    
    try {
      await createStripeSessionAndRedirect(addon.packageId, "addon");
    } catch (error) {
      console.error('Addon purchase error:', error);
      alert('Error processing purchase. Please try again.');
    } finally {
      setLoadingStates(prev => ({ ...prev, [addon.title]: false }));
    }
  };

  const isCurrentPlan = (planTitle) => {
    return currentSubscription?.subscription_name === planTitle;
  };

  const isLoading = (itemTitle) => {
    return loadingStates[itemTitle] || false;
  };

  return (
    <div className="bg-black text-white py-20 px-4 sm:px-8 lg:px-16">
      <h2 className="text-3xl sm:text-4xl font-bold text-center mb-14">
        Choose Your Plan
      </h2>
      
      {currentSubscription && (
        <div className="text-center mb-8">
          <p className="text-lg text-gray-300">
            Current Plan: <span className="text-green-400 font-semibold">{currentSubscription.subscription_name}</span>
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-7xl mx-auto mb-20">
        {subscriptionPlans.map((plan, idx) => (
          <div
            key={idx}
            className={`rounded-xl p-6 border transition duration-300 ${
              plan.mostPopular
                ? "border-green-400 shadow-lg bg-gradient-to-b from-zinc-900 to-black"
                : isCurrentPlan(plan.title)
                ? "border-blue-400 bg-gradient-to-b from-blue-900/20 to-black"
                : "border-gray-700"
            }`}
          >
            <h3 className="text-xl font-semibold mb-1">{plan.title}</h3>
            <div className="text-3xl font-bold mb-1">
              {plan.price}
              <span className="text-base font-normal"> /month</span>
            </div>
            <p className="text-gray-400 text-sm mb-5">{plan.description}</p>
            
            <button
              className={`font-bold py-2 px-4 rounded mb-6 w-full transition duration-300 ${
                isCurrentPlan(plan.title)
                  ? "bg-blue-500 text-white cursor-not-allowed"
                  : "bg-green-400 hover:bg-green-500 text-black"
              }`}
              onClick={() => handleSubscriptionClick(plan)}
              disabled={isLoading(plan.title) || isCurrentPlan(plan.title)}
            >
              {isLoading(plan.title) ? "Processing..." : isCurrentPlan(plan.title) ? "Current Plan" : plan.price === "$0" ? "Get Started" : "Subscribe"}
            </button>
            
            <ul className="text-sm space-y-2">
              {plan.features.map((feature, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="text-green-400 mt-1">✔</span>
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      {/* Pay-Per-Use Add-Ons */}
      <h2 className="text-3xl sm:text-4xl font-bold text-center mb-10">
        Pay-Per-Use Add-Ons
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-7xl mx-auto">
        {payPerUseAddOns.map((addon, idx) => (
          <div
            key={idx}
            className="rounded-xl p-6 border border-gray-700 transition duration-300"
          >
            <h3 className="text-xl font-semibold mb-1">{addon.title}</h3>
            <div className="text-2xl font-bold mb-4">{addon.price}</div>
            <button 
              className="bg-green-400 hover:bg-green-500 text-black font-bold py-2 px-4 rounded mb-6 w-full transition duration-300"
              onClick={() => handleAddonClick(addon)}
              disabled={isLoading(addon.title)}
            >
              {isLoading(addon.title) ? "Processing..." : "Purchase"}
            </button>
            <ul className="text-sm space-y-2">
              {addon.features.map((feature, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="text-green-400 mt-1">✔</span>
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PricingSection;
